import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { Pagination } from '@gerdau/hefesto-mais-web';
import { Button } from '@gerdau/hefesto';

import CartProductTable, { OptionsProps } from './CartProductTable';
import CartHeader, { CartHeaderGraph } from './CartHeader';
import { PaginationContainer, ShoppingCartContainer } from './style';
import {
  CartOptions,
  ErrorProps,
  ItemTableProps,
  MeasureUnit,
  MinimunOrderItemProps,
  NewCartItem,
  SlicePaginationProps,
  UpdateItemProps,
  ValidationModalProps
} from '../../../../services/MVP/models';
import { SummaryBottom } from './SummaryBottom';
import { EMeasureUnit } from '../../../../store/commomTypes';
import { useCart } from './useCart';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import MessageChooseItem from '../../../../components/MessageChooseItem';
import { useHistory, useLocation } from 'react-router-dom';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { hasDuplicates } from './validate';
import { ValidationModal } from './ValidationModal';
import { ERoutes } from '../../../../routes';
import moment from 'moment';
import {
  orderSimulateRequest,
  orderSimulateRequestSuccess,
  updateCheckoutLocalStorage
} from '../../../../store/ducks/order-simulate-checkout-mvp/actions';
import {
  ICheckoutResponse,
  IDataOrderSimulate
} from '../../../../store/ducks/order-simulate-checkout-mvp/types';
import { useCartService } from '../../../../services/MVP/useCartService';
import { CheckoutServiceMvp } from '../../../../services/CheckoutMvp';
import { EPurchaseRoutes } from '../../purchase.routes';
import { ICustomer } from '../../../../store/ducks/customers/types';
import { BreadcrumbItemProps } from '@gerdau/hefesto-mais-web/Breadcrumbs/Breadcrumbs.types';
import { ELocalStorageKey } from '../../../../services/EGerdauService/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../store/ducks/tagging/types';
import {
  parseNumber,
  paymentAnticipated,
  purchaseOrigin
} from '../../../../services/MVP/utils';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { normalizeQuantity } from '../../../../utils/purchaseHelpers';
import { useShoppingCart } from '../../../../providers/shoppingcart.provider';
import { EOrderSettingsRoutes } from '../OrderSettingsMvp/orderSettings.routes';

const sliceItems = ({ cartItems, page, size }: SlicePaginationProps) => {
  return cartItems.slice((page - 1) * size, page * size);
};

const CartPagination = ({
  handleChange,
  total,
  loading
}: {
  handleChange: (page: number, size: number) => void;
  total: number;
  loading: boolean;
}) => {
  if (loading && total === 0) {
    return null;
  }
  return (
    <PaginationContainer disableNextPage={total <= 12}>
      <Pagination
        ariaLabel="pagination"
        onChange={(page: number, pageTotal: number) =>
          total <= 12 ? null : handleChange(page, pageTotal)
        }
        total={total}
        showTotal
        pageSizeOptions={[12, 24, 30]}
      />
    </PaginationContainer>
  );
};

const ShoppingCartMvp: React.FC = () => {
  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);
  const history = useHistory();
  const tipo_compra = purchaseOrigin();
  const {
    cartId,
    cartData,
    getProductsInformation,
    addBulkGetCart,
    deleteAllCartItems
  } = useCart();
  const { getCart, deleteItem } = useCartService();
  const { dirtyCart, updateDirtyCartState } = useShoppingCart();
  const shoppingCartRef = useRef<HTMLDivElement>(null);
  const [goToCheckout, setGoToCheckout] = useState(false);
  const [cartItems, setCartItems] = useState<NewCartItem[]>([]);
  const [dirtyCartItems, setDirtyCartItems] = useState<NewCartItem[]>([]);
  const [minimunOrder, setMinimunOrder] = useState<MinimunOrderItemProps[]>([]);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [itemsTable, setItemsTable] = useState<ItemTableProps>({
    items: [],
    page: 1,
    size: 12
  });
  const [validationModalInformation, setValidationModalInformation] =
    useState<ValidationModalProps>({
      show: false,
      title: '',
      description: '',
      handleNextBtn: () => null,
      handleNextBtnTitle: 'Continuar',
      hiddenLeftButton: false
    });
  const [cartLoading, setCartLoading] = useState(false);

  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const shoppingCart = useSelector(
    (appState: ApplicationState) => appState.longSteelShoppingCart
  );
  const checkoutState = useSelector(
    (appState: ApplicationState) => appState.orderSimulateCheckoutMvp
  );

  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCart.emitterId
  ) as ICustomer;

  const isBlockedCustomer =
    customer?.blocks &&
    customer?.blocks?.length > 0 &&
    customer.blocks.find((blc) => blc.type === 'financial');

  const dispatch = useDispatch();

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById;

  const loading = cartLoading || customerLoading;

  const handleServiceUnavailable = ({
    noClean
  }: { noClean?: boolean } = {}) => {
    dispatch(
      sendTagging({
        event: 'servico_indisponivel',
        screenName: 'carrinho',
        tipo_compra: tipo_compra,
        mensagem: 'Serviço indisponível temporariamente'
      })
    );

    setValidationModalInformation({
      title: 'Serviço indisponível temporariamente',
      description:
        'Tente novamente mais tarde ou entre em contato com o seu vendedor.',
      show: true,
      hiddenLeftButton: true,
      handleNextBtn: handleCloseValidationModal,
      handleNextBtnTitle: 'Continuar'
    });

    if (noClean) {
      return;
    }
    setCartItems([]);
    setDirtyCartItems([]);
    setItemsTable((prev) => ({
      page: 1,
      size: prev.size,
      items: []
    }));
    setSelectedItems([]);
    setGoToCheckout(false);
  };

  useEffect(() => {
    (async () => {
      try {
        setCartLoading(true);

        const bodyRequest = {
          soldTo: shoppingCart.emitterId,
          shipTo: shoppingCart.receiverId
        };

        const hasBodyKeyEmpty = Object.values(bodyRequest).some(
          (item) => !item
        );

        if (
          !customerLoading &&
          !hasBodyKeyEmpty &&
          customer?.productsDescriptionResponse &&
          cartId
        ) {
          const data = await cartData();

          if (!data || data.hasError) {
            throw new Error();
          }

          if (data && Array.isArray(data.items)) {
            const cartProducts = await getProductsInformation({
              items: data.items
            });

            if (cartProducts.length !== data.items.length) {
              throw new Error();
            }

            const slicedItems = sliceItems({
              cartItems: cartProducts,
              page: 1,
              size: itemsTable.size
            });

            setCartItems(cartProducts);
            setDirtyCartItems(cartProducts);
            setItemsTable((prev) => ({
              page: 1,
              size: prev.size,
              items: slicedItems
            }));
            setSelectedItems([]);
            setMinimunOrder(data.minimun ?? []);

            const taggingProduct: ITaggingParams = {
              event: 'view_cart',
              ecommerce: {
                currency: 'BRL',
                value: getTotal(cartProducts),
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                paginacao: itemsTable.page.toString(),
                items: cartProducts.map((item) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: item?.sku?.toString(),
                  item_name: item.name,
                  price: item.price?.creditPrice,
                  unidade_de_medida:
                    item.extension_attributes.measure_unity_gerdau,
                  quantity: item.qty.toString(),
                  data_desejada: item.extension_attributes.expected_date_gerdau,
                  pagamento: item.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    item.extension_attributes.tax_benefit_gerdau,
                  destinacao: item.extension_attributes.destination_gerdau,
                  item_category: item?.descricao_gpm ?? ''
                }))
              }
            };
            dispatch(sendTagging(taggingProduct));
            return;
          }

          throw new Error();
        }
      } catch (error) {
        console.error(error);
        handleServiceUnavailable();
      } finally {
        setCartLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (dirtyCartItems) {
      const hasBlockedPaymentType = dirtyCartItems.some(
        (item) =>
          !paymentAnticipated.includes(
            item.extension_attributes?.payment_type_gerdau
          )
      );
      if (isBlockedCustomer && hasBlockedPaymentType) {
        setValidationModalInformation({
          title: 'Condição de pagamento indisponível',
          description:
            'Selecione uma condição de pagamento antecipada ou entre em contato com o seu vendedor.',
          show: true,
          hiddenLeftButton: true,
          handleNextBtn: handleCloseValidationModal,
          handleNextBtnTitle: 'Continuar'
        });
      }
    }
  }, [isBlockedCustomer, dirtyCartItems]);

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const selectedProducts = cartItems.map((product) => product.item_id ?? 0);

      setSelectedItems(selectedProducts);
      return;
    }

    setSelectedItems([]);
    return;
  };

  const handleSelectProduct = (
    e: ChangeEvent<HTMLInputElement>,
    item_id: number
  ) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, item_id]);
      return;
    }

    const selectedProducts = selectedItems.filter(
      (selected) => selected !== item_id
    );
    setSelectedItems(selectedProducts);
    return;
  };

  const getTotal = (items: NewCartItem[]) => {
    const total = items.reduce((total, item) => {
      const destinationOptions = Object.values(item.cartOptions).map(
        (destinationOpt: CartOptions) => ({
          value: destinationOpt.destination.code,
          label: destinationOpt.destination.description
        })
      );

      const destination =
        item.extension_attributes?.destination_gerdau ??
        destinationOptions[0].value;

      const optionsByDestination = item.cartOptions[destination];

      const price = item.price;

      const unity = item.extension_attributes.measure_unity_gerdau;
      const currentMeasureUnit = optionsByDestination.measureUnits.find(
        (unit) => unit.measure === unity
      ) as MeasureUnit;

      const productDate = item.extension_attributes.expected_date_gerdau;
      const salesAgreementData = price.salesAgreementData;
      const endDate = salesAgreementData.expirationDate
        ? salesAgreementData.expirationDate.replace('Z', '')
        : salesAgreementData.expirationDate;

      const salesAgreementExpirationDate =
        !moment(productDate).isAfter(endDate);
      const agreementData = {
        destination: salesAgreementData.destination,
        payment: salesAgreementData.paymentTerms,
        expirationDate: endDate
      };

      const hasAgreement = () => {
        const hasAnyAgreementKeyEmpty = Object.values(agreementData).some(
          (item) => !item
        );
        if (salesAgreementData && !hasAnyAgreementKeyEmpty) {
          return (
            salesAgreementData.destination === destination &&
            salesAgreementData.paymentTerms ===
              item.extension_attributes.payment_type_gerdau &&
            salesAgreementExpirationDate
          );
        }

        return false;
      };

      const priceWithoutMeasure =
        hasAgreement() && salesAgreementData.salesAgreement
          ? salesAgreementData.salesAgreement
          : Number(price.totalPrice ?? price.creditPrice) /
            normalizeQuantity(price.quantity);

      const convertPriceMeasure = optionsByDestination.measureUnits.find(
        (priceOpt) => priceOpt.measure === price.um
      );

      const priceKg =
        priceWithoutMeasure /
        parseNumber(convertPriceMeasure?.convMeasureKg ?? '1');

      const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
      const minimunOrderQty =
        measureUnitminQty !== 1 && item.qty === 1
          ? Math.ceil(measureUnitminQty / Number(currentMeasureUnit.multiple)) *
            Number(currentMeasureUnit.multiple)
          : Number(currentMeasureUnit.multiple) * item.qty;

      const multipleWithKG =
        parseNumber(currentMeasureUnit.convMeasureKg ?? '1') * minimunOrderQty;

      const itemPrice = priceKg * multipleWithKG;

      return total + itemPrice;
    }, 0);
    return total.toFixed(2);
  };

  const handleChangePagination = (page: number, size: number) => {
    const slicedItems = sliceItems({ cartItems, page, size });
    setItemsTable({ items: slicedItems, page, size });
  };

  const handleUpdateItem = async ({
    item_id,
    type,
    value
  }: UpdateItemProps) => {
    const item = cartItems.find((cartItem) => cartItem.item_id === item_id);
    if (!value || !item) {
      return;
    }
    updateDirtyCartState(
      dirtyCart.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      )
    );
    // setViewCartTag(true);
    setItemsTable((prev) => ({
      ...itemsTable,
      items: prev.items.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      )
    }));
    setCartItems((cartItemsPrev) =>
      cartItemsPrev.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      )
    );
  };

  const handleDuplicateItem = async (id: number) => {
    if (validations.maximun) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho atingiu o limite de 30 produtos.'
        })
      );
      return dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho atingiu o limite de 30 produtos.',
          durationInMS: 3000,
          labelActionOne: 'X'
        })
      );
    }

    try {
      setCartLoading(true);

      const item = cartItems.find(
        (cartItem) => cartItem.item_id === id
      ) as NewCartItem;

      const itemIndex =
        cartItems.findIndex((cartItem) => cartItem.item_id === id) ?? 0;

      const itemToDuplicate = {
        ...item,
        extension_attributes: {
          ...item.extension_attributes,
          expected_date_gerdau: ''
        }
      };

      // delete item_id to method BULK create a new item on cart with item_id
      delete itemToDuplicate.item_id;

      cartItems.splice(itemIndex + 1, 0, itemToDuplicate);

      const newItems = cartItems.map((product) => {
        const extensionAtt = product.extension_attributes;
        const destination = extensionAtt.destination_gerdau;
        const cartOptions = product.cartOptions[destination];
        const itemUnits = cartOptions.measureUnits;

        const unit = product.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;
        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && product.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * product.qty * 1000
              ) / 1000;

        return {
          ...product,
          qty: minimunOrderQty
        };
      });

      const data = await addBulkGetCart({
        items: newItems,
        minimumExisting: minimunOrder
      });

      if (
        (data && data.cart && data.cart.hasError) ||
        data.delete !== true ||
        typeof data.delete === 'object' ||
        !data
      ) {
        throw new Error();
      }

      if (data && Array.isArray(data.cart.items)) {
        const cartProducts = await getProductsInformation({
          items: data.cart.items,
          withInformation: cartItems
        });

        if (cartProducts.length !== data.cart.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.cart.minimun ?? []);
        return;
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      setCartLoading(true);

      //Return true when has sucess on delete
      const removeItem = await deleteItem(id);

      //Case has error show error modal
      if (
        removeItem !== true ||
        !removeItem ||
        typeof removeItem === 'object'
      ) {
        throw new Error();
      }

      //If has only 1 item and removeItem is sucess then clean cart
      if (cartItems.length === 1) {
        setCartItems([]);
        setDirtyCartItems([]);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: []
        }));
        setSelectedItems([]);

        //Case has sucess dispatch tagging
        dispatch(
          sendTagging({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'BRL',
              value: '0',
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              items: cartItems
                .filter((cartItem) => cartItem.item_id === id)
                .map((deletedProduct) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
            }
          })
        );
        return;
      }

      const items = cartItems.filter((cartItem) => cartItem.item_id !== id);

      const newItems = [...items].map((product) => {
        const extensionAtt = product.extension_attributes;
        const destination = extensionAtt.destination_gerdau;
        const cartOptions = product.cartOptions[destination];
        const itemUnits = cartOptions.measureUnits;

        const unit = product.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;
        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && product.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * product.qty * 1000
              ) / 1000;

        return {
          ...product,
          qty: minimunOrderQty
        };
      });

      if (newItems.length) {
        //Case has sucess dispatch tagging
        dispatch(
          sendTagging({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'BRL',
              value: getTotal(items),
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              items: cartItems
                .filter((cartItem) => cartItem.item_id === id)
                .map((deletedProduct) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
            }
          })
        );

        //Update rest items on cart with bulk and bulk return items updated
        const data = await addBulkGetCart({ items: newItems });

        //Case return with error show error modal
        if (
          (data && data.cart && data.cart.hasError) ||
          data.delete !== true ||
          typeof data.delete === 'object' ||
          !data
        ) {
          throw new Error();
        }

        //Case has sucess get information from products on magento API
        if (data && Array.isArray(data.cart.items)) {
          const cartProducts = await getProductsInformation({
            items: data.cart.items,
            withInformation: cartItems
          });

          //Case return with error show error modal
          if (cartProducts.length !== data.cart.items.length) {
            throw new Error();
          }

          const slicedItems = sliceItems({
            cartItems: cartProducts,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(cartProducts);
          setDirtyCartItems(cartProducts);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);
          setMinimunOrder(data.cart.minimun ?? []);
          return;
        }

        //Show modal error when no has data or dont is a Array on data.cart.items
        throw new Error();
      } else {
        //Case wish delete last item from cart cal DELETE ALL route
        const data = await deleteAllCartItems();

        //Case return with error show error modal
        if (data.delete !== true || !data || typeof data.delete === 'object') {
          throw new Error();
        }

        //Case has sucess dispatch tagging and clean cart
        if (data.delete) {
          dispatch(
            sendTagging({
              event: 'remove_from_cart',
              ecommerce: {
                currency: 'BRL',
                value: getTotal(cartItems),
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                items: cartItems.map((deletedProduct) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
              }
            })
          );
          setCartItems([]);
          setDirtyCartItems([]);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: []
          }));
          setSelectedItems([]);
          return;
        }
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleDeleteAllItems = async () => {
    try {
      setCartLoading(true);
      //Case mark to delete all items
      if (cartItems.length === selectedItems.length) {
        const data = await deleteAllCartItems();

        //Case return error on try delete method
        if (data.delete !== true || !data || typeof data.delete === 'object') {
          throw new Error();
        }

        //Case has sucess dispatch tagging and clean cart
        if (data.delete) {
          dispatch(
            sendTagging({
              event: 'remove_from_cart',
              ecommerce: {
                currency: 'BRL',
                value: getTotal(cartItems),
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                items: cartItems.map((deletedProduct) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
              }
            })
          );
          setCartItems([]);
          setDirtyCartItems([]);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: []
          }));
          setSelectedItems([]);
        }
        return;
      }

      //Case NOT mark all items to delete
      const itemsWithoutDelete = cartItems
        .filter(
          (item) =>
            item.item_id !==
            selectedItems.find((tableItem) => tableItem === item.item_id)
        )
        .map((product) => {
          const extensionAtt = product.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = product.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = product.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && product.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * product.qty * 1000
                ) / 1000;

          delete product.item_id;

          return {
            ...product,
            qty: minimunOrderQty
          };
        });

      //Case items length is all cart
      if (itemsWithoutDelete.length === 0) {
        const data = await deleteAllCartItems();
        if (data.delete !== true || !data || typeof data.delete === 'object') {
          throw new Error();
        }
        if (data.delete) {
          setCartItems([]);
          setDirtyCartItems([]);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: []
          }));
          setSelectedItems([]);
        }
        setCartLoading(false);
        return;
      }

      //On momment no have has API to delete part from cart
      //Then delete all and insert rest
      const dataDelete = await deleteAllCartItems();

      //Case has error show error modal
      if (
        dataDelete.delete !== true ||
        !dataDelete ||
        typeof dataDelete.delete === 'object'
      ) {
        throw new Error();
      }

      //When has sucess on delete return true
      if (dataDelete.delete) {
        //Prepare to dispatch tagging
        const itemsDeleted = cartItems.filter(
          (item) =>
            item.item_id ===
            selectedItems.find((tableItem) => tableItem === item.item_id)
        );

        //Dispatch tagging with items deleted
        dispatch(
          sendTagging({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'BRL',
              value: getTotal(itemsDeleted),
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              items: itemsDeleted.map((deletedProduct) => ({
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                item_id: deletedProduct?.sku?.toString(),
                item_name: deletedProduct.name,
                price: deletedProduct.price?.creditPrice,
                unidade_de_medida:
                  deletedProduct.extension_attributes.measure_unity_gerdau,
                quantity: deletedProduct.qty.toString(),
                data_desejada:
                  deletedProduct.extension_attributes.expected_date_gerdau,
                pagamento:
                  deletedProduct.extension_attributes.payment_type_gerdau,
                beneficio_fiscal:
                  deletedProduct.extension_attributes.tax_benefit_gerdau,
                destinacao:
                  deletedProduct.extension_attributes.destination_gerdau,
                item_category: deletedProduct.product_type
              }))
            }
          })
        );

        //Add and update rest items without delete
        const data = await addBulkGetCart({ items: itemsWithoutDelete });

        //Case has error show error modal
        if (
          (data && data.cart && data.cart.hasError) ||
          data.delete !== true ||
          typeof data.delete === 'object' ||
          !data
        ) {
          throw new Error();
        }

        if (data && Array.isArray(data.cart.items)) {
          const cartProducts = await getProductsInformation({
            items: data.cart.items,
            withInformation: cartItems
          });

          //Case return from products informations length dont is equal to cart length
          if (cartProducts.length !== data.cart.items.length) {
            throw new Error();
          }

          const slicedItems = sliceItems({
            cartItems: cartProducts,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(cartProducts);
          setDirtyCartItems(cartProducts);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);
          setMinimunOrder(data.cart.minimun ?? []);
          return;
        }

        //Case dont match with anyway
        throw new Error();
      }
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleUpdateAllItems = async ({
    date,
    payment,
    fiscalBenefits,
    destination,
    updatePrice
  }: OptionsProps) => {
    try {
      setCartLoading(true);

      //If call this function to update price options;
      if (updatePrice) {
        //Prepare data to update all stats from carts items
        const newItems = cartItems.map((product) => {
          const extensionAtt = product.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = product.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = product.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && product.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * product.qty * 1000
                ) / 1000;

          return {
            ...product,
            qty: minimunOrderQty
          };
        });

        //Call function to update all
        const data = await addBulkGetCart({
          items: newItems,
          minimumExisting: minimunOrder
        });

        //When has any error on update function show modal error
        if (
          (data && data.cart && data.cart.hasError) ||
          data.delete !== true ||
          typeof data.delete === 'object' ||
          !data
        ) {
          throw new Error();
        }

        //if return sucess enter on step to get information of product
        if (data && Array.isArray(data.cart.items)) {
          const cartProducts = await getProductsInformation({
            items: data.cart.items,
            withInformation: cartItems
          });

          //When has any error on get information show modal error
          if (cartProducts.length !== data.cart.items.length) {
            throw new Error();
          }

          const slicedItems = sliceItems({
            cartItems: cartProducts,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(cartProducts);
          setDirtyCartItems(cartProducts);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);
          setMinimunOrder(data.cart.minimun ?? []);
          return;
        }

        //When is updatePrice and dont match with anyway
        throw new Error();
      }

      const itemsAdjust = cartItems.filter((cart) => {
        if (selectedItems.length === cartItems.length) {
          return true;
        }

        return cart.item_id && selectedItems.includes(cart.item_id);
      });

      const newItems = [...itemsAdjust].map((item) => {
        const canChangeDestination =
          destination &&
          Object.keys(item.cartOptions).some((dest) => dest === destination);

        const canChangeFiscalBenefits =
          fiscalBenefits &&
          item.cartOptions[
            item.extension_attributes.destination_gerdau
          ].fiscalBenefits.some((fiscal) => fiscal.code === fiscalBenefits);

        return {
          ...item,
          extension_attributes: {
            ...item.extension_attributes,
            ...(date && {
              expected_date_gerdau: moment(date).format('YYYY-MM-DD')
            }),
            ...(payment && { payment_type_gerdau: payment }),
            ...(destination &&
              canChangeDestination && { destination_gerdau: destination }),
            ...(fiscalBenefits &&
              canChangeFiscalBenefits && { tax_benefit_gerdau: fiscalBenefits })
          }
        };
      });

      const oldItems = cartItems.filter(
        (old) => old.item_id && !selectedItems.includes(old.item_id)
      );

      const newCartItems = [
        ...newItems,
        ...(selectedItems.length === cartItems.length ? [] : oldItems)
      ].map((product) => {
        const extensionAtt = product.extension_attributes;
        const destination = extensionAtt.destination_gerdau;
        const cartOptions = product.cartOptions[destination];
        const itemUnits = cartOptions.measureUnits;

        const unit = product.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;
        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && product.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * product.qty * 1000
              ) / 1000;

        return {
          ...product,
          qty: minimunOrderQty
        };
      });

      const data = await addBulkGetCart({
        items: newCartItems,
        minimumExisting: minimunOrder
      });

      if (
        (data && data.cart && data.cart.hasError) ||
        data.delete !== true ||
        typeof data.delete === 'object' ||
        !data
      ) {
        throw new Error();
      }

      if (data && Array.isArray(data.cart.items)) {
        const cartProducts = await getProductsInformation({
          items: data.cart.items,
          withInformation: cartItems
        });

        if (cartProducts.length !== data.cart.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.cart.minimun ?? []);
        return;
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const generateGraph = (products: NewCartItem[]) => {
    const minimunOrderRules = minimunOrder.map((min) => ({
      unit: min.unit,
      type: min.gpm ?? 'Peso do carrinho',
      name: min.name ?? 'Peso do carrinho',
      minQty: min.qty,
      cartQty: 0
    }));

    const cartHeaderGraphData: CartHeaderGraph[] = products.reduce(
      (acc: CartHeaderGraph[], item) => {
        const cartQty = item.qty;
        const minQty = 0;
        const type = item.sap_descricao_gpm ?? 'Peso do carrinho';

        const destinationOptions = Object.values(item.cartOptions).map(
          (destinationOpt: CartOptions) => destinationOpt.destination.code
        );
        const destination =
          item.extension_attributes?.destination_gerdau ??
          destinationOptions[0];

        if (Object.values(item.cartOptions).length === 0) {
          return [];
        }

        const optionsByDestination = item.cartOptions[destination];
        const itemUnits = optionsByDestination.measureUnits;
        const unit = item.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;

        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && item.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * item.qty * 1000
              ) / 1000;

        const hasItemType = minimunOrderRules.some(
          (minItem) => minItem.type === type
        );

        const newItem = {
          cartQty,
          minQty,
          unit,
          type: hasItemType ? type : 'Peso do carrinho'
        };

        const existingItem = acc.find(
          (accItem) => accItem.type === newItem.type
        );

        if (
          !currentMeasureUnit ||
          !currentMeasureUnit.convMeasureKg ||
          !currentMeasureUnit.convMeasureTo
        ) {
          return acc;
        }

        if (existingItem) {
          const minimunKg =
            minimunOrderQty * parseNumber(currentMeasureUnit.convMeasureKg);
          const convertType =
            existingItem?.unit.toUpperCase() === 'KG'
              ? minimunKg
              : minimunKg / 1000;
          existingItem.cartQty += convertType;
        }

        return acc;
      },
      minimunOrderRules
    );

    if (minimunOrder.length > 0 && cartHeaderGraphData.length === 0) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Falha ao buscar as opções dos produtos'
        })
      );
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar as opções dos produtos',
          labelActionOne: 'Atualizar',
          handleClickActionOne: () => history.go(0),
          durationInMS: 3000
        })
      );
    }

    return cartHeaderGraphData;
  };

  const validations = {
    minimun: generateGraph(cartItems).some(
      (graph) => graph.minQty > graph.cartQty
    ),
    maximun: cartItems.length === 30
  };

  const handleCloseValidationModal = () => {
    setValidationModalInformation((prev) => ({
      ...prev,
      show: false
    }));
  };

  const handleNavigateSelectingItems = () => {
    history.push(`${EOrderSettingsRoutes.SEARCH_ITEMS}`);
  };

  const handleNext = async () => {
    const multipleSameProducts = hasDuplicates(cartItems);

    const itemsCheckErrors: NewCartItem[] = cartItems.map((item) => {
      const withoutDate = !item.extension_attributes.expected_date_gerdau;
      const withoutPayment = !item.extension_attributes.payment_type_gerdau;
      const withoutDestination = !item.extension_attributes.destination_gerdau;

      const paymentBlocked =
        !!isBlockedCustomer &&
        !paymentAnticipated.includes(
          item.extension_attributes.payment_type_gerdau
        );

      if (
        withoutDate ||
        withoutPayment ||
        paymentBlocked ||
        withoutDestination
      ) {
        return {
          ...item,
          errors: {
            ...item.errors,
            date: withoutDate,
            payment: withoutPayment || paymentBlocked,
            destination: withoutDestination
          }
        };
      }

      return item;
    });

    const itemsWithErrors = itemsCheckErrors.filter((item) =>
      Object.values(item.errors as ErrorProps).some((error) => error)
    );

    if (itemsWithErrors.length) {
      const cartItemsSort = itemsCheckErrors.sort((a, b) => {
        const aHasError = Object.values(a.errors as ErrorProps).some(
          (error) => error
        );
        const bHasError = Object.values(b.errors as ErrorProps).some(
          (error) => error
        );

        if (aHasError && !bHasError) return -1;
        if (!aHasError && bHasError) return 1;
        return 0;
      });

      const slicedItems = sliceItems({
        cartItems: cartItemsSort,
        page: 1,
        size: itemsTable.size
      });

      setCartItems(cartItemsSort);
      setDirtyCartItems(cartItemsSort);
      setItemsTable((prev) => ({
        page: 1,
        size: prev.size,
        items: slicedItems
      }));
      setSelectedItems([]);

      // setViewCartTag(true);
      if (shoppingCartRef.current) {
        shoppingCartRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      if (itemsWithErrors.find((err) => err.errors?.payment)) {
        dispatch(
          sendTagging({
            event: 'pagamento_indisponivel',
            screenName: 'carrinho',
            tipo_compra: tipo_compra
          })
        );
        setValidationModalInformation({
          title: 'Condição de pagamento indisponível',
          description:
            'Selecione uma condição de pagamento antecipada ou entre em contato com o seu vendedor.',
          show: true,
          hiddenLeftButton: true,
          handleNextBtn: handleCloseValidationModal,
          handleNextBtnTitle: 'Continuar'
        });

        return;
      }
      return;
    }

    if (validations.minimun) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho precisa de uma quantidade mínima de produtos.'
        })
      );

      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho precisa de uma quantidade mínima de produtos.',
          labelActionOne: 'Adicionar produto',
          handleClickActionOne: () => handleNavigateSelectingItems(),
          durationInMS: 3000
        })
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (multipleSameProducts) {
      setValidationModalInformation({
        hiddenLeftButton: false,
        show: true,
        title: 'Produtos idênticos encontrados',
        description:
          'As quantidades dos produtos idênticos serão somadas. Deseja continuar?',
        handleNextBtn: handleToCheckout,
        handleNextBtnTitle: 'Continuar'
      });

      return;
    }

    return handleToCheckout();
  };

  const handleToCheckout = async () => {
    try {
      setCartLoading(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const multipleSameProducts = hasDuplicates(cartItems);

      const productsDuplicateEqual = cartItems.reduce((acc, item) => {
        const itemAlreadyExist = acc.find((accItem) => {
          const accAtt = accItem.extension_attributes;
          const itemAtt = item.extension_attributes;

          const sameDate = moment(accAtt.expected_date_gerdau).isSame(
            itemAtt.expected_date_gerdau
          );

          return (
            accItem.sku === item.sku &&
            accAtt.measure_unity_gerdau === itemAtt.measure_unity_gerdau &&
            sameDate &&
            accAtt.destination_gerdau === itemAtt.destination_gerdau &&
            accAtt.payment_type_gerdau === itemAtt.payment_type_gerdau &&
            accAtt.tax_benefit_gerdau === itemAtt.tax_benefit_gerdau
          );
        });

        if (itemAlreadyExist) {
          const newAcc = acc.filter(
            (oldAcc) =>
              !(
                oldAcc.sku === itemAlreadyExist.sku &&
                oldAcc.extension_attributes.measure_unity_gerdau ===
                  itemAlreadyExist.extension_attributes.measure_unity_gerdau &&
                oldAcc.extension_attributes.expected_date_gerdau ===
                  itemAlreadyExist.extension_attributes.expected_date_gerdau &&
                oldAcc.extension_attributes.destination_gerdau ===
                  itemAlreadyExist.extension_attributes.destination_gerdau &&
                oldAcc.extension_attributes.payment_type_gerdau ===
                  itemAlreadyExist.extension_attributes.payment_type_gerdau &&
                oldAcc.extension_attributes.tax_benefit_gerdau ===
                  itemAlreadyExist.extension_attributes.tax_benefit_gerdau
              )
          );
          return [
            ...newAcc,
            {
              ...itemAlreadyExist,
              qty: itemAlreadyExist.qty + item.qty
            }
          ];
        }

        return [...acc, item];
      }, [] as NewCartItem[]);

      const convertItems = productsDuplicateEqual
        .filter(
          (prd) =>
            Object.values(prd.cartOptions).length > 0 &&
            prd.extension_attributes &&
            prd.extension_attributes.destination_gerdau
        )
        .map((item) => {
          const extensionAtt = item.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = item.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = item.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && item.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * item.qty * 1000
                ) / 1000;

          if (multipleSameProducts) {
            // need delete item_id to create new items
            delete item.item_id;
          }

          return {
            ...item,
            qty: minimunOrderQty
          };
        });

      const data = await addBulkGetCart({
        items: convertItems,
        withoutMimimum: true,
        minimumExisting: minimunOrder,
        ...(multipleSameProducts && {
          itemsToDelete: cartItems.map((item) => ({
            item_id: item.item_id ?? 0
          }))
        }),
        complete: true
      });

      if (
        (data && data.cart && data.cart.hasError) ||
        data.delete !== true ||
        typeof data.delete === 'object' ||
        !data
      ) {
        throw new Error();
      }

      if (data && Array.isArray(data.cart.items)) {
        const itemsWithInformation = await getProductsInformation({
          items: data.cart.items,
          withInformation: cartItems
        });
        const slicedItems = sliceItems({
          cartItems: itemsWithInformation,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(itemsWithInformation);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
      }

      //START FUNCTION TO GO CHECKOUT
      const checkoutServiceMvp = new CheckoutServiceMvp();

      if (data.complete) {
        const param: IDataOrderSimulate = {
          cartNumber: cartId,
          createdAt: data.complete.created_at,
          updatedAt: data.complete.updated_at,
          purchaseNumber: '', // preenchido pelo usuário na outra tela
          shipTo: shoppingCart.receiverId,
          soldTo: shoppingCart.emitterId,
          items: data.cart.items
            .filter(
              (prd) =>
                Object.values(prd.cartOptions).length > 0 &&
                prd.extension_attributes &&
                prd.extension_attributes.destination_gerdau
            )
            .map((product) => {
              const extensionAtt = product.extension_attributes;
              const destination = extensionAtt.destination_gerdau;
              const cartOptions = product.cartOptions[destination];
              const itemUnits = cartOptions.measureUnits;
              const unit = product.extension_attributes
                .measure_unity_gerdau as EMeasureUnit;
              const currentMeasureUnit = itemUnits.find(
                (unity) => unity.measure === unit
              ) as MeasureUnit;

              const measureUnitminQty = Number(
                currentMeasureUnit.minimumQuantity
              );
              const minimunOrderQty =
                measureUnitminQty !== 1 && product.qty === 1
                  ? Math.round(
                      Math.ceil(
                        measureUnitminQty / Number(currentMeasureUnit.multiple)
                      ) *
                        Number(currentMeasureUnit.multiple) *
                        1000
                    ) / 1000
                  : Math.round(
                      Number(currentMeasureUnit.multiple) * product.qty * 1000
                    ) / 1000;

              const benefit =
                product.extension_attributes.tax_benefit_gerdau ?? '';

              return {
                item_id_gerdau: '',
                name: product.name,
                customerPurchaseItem: '',
                material: product.sku,
                unityMeasure: product.extension_attributes.measure_unity_gerdau,
                requiredDate:
                  moment(product.extension_attributes.expected_date_gerdau)
                    .local()
                    .toISOString() ?? '',
                quantity: minimunOrderQty.toString(),
                term: product.extension_attributes.payment_type_gerdau,
                benefit: benefit && benefit !== 'nenhum' ? benefit : '',
                plant: Number(
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].plant
                ),
                item_category:
                  cartItems.find((item) => item.sku === product.sku)
                    ?.descricao_gpm ?? '',
                salesGroup:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesGroup,
                salesOffice:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesOffice,
                salesOrg:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesOrg,
                distrChannel: Number(
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].distributionChannel
                ),
                activitySector:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].division,
                industrialType:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].destination.code,
                consumptionOrder: '',
                price_agreement_date_gerdau:
                  product.extension_attributes.price_agreement_date_gerdau ??
                  '',
                sku: product.sku,
                paymentTerms:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].paymentTerms,
                measureUnits: product.cartOptions[
                  product.extension_attributes.destination_gerdau
                ].measureUnits.map((measureUnit) => ({
                  convMeasureKg: measureUnit.convMeasureKg,
                  convMeasureTo: measureUnit.convMeasureTo,
                  measure: measureUnit.measure as EMeasureUnit,
                  minimumQuantity: measureUnit.minimumQuantity,
                  multiple: measureUnit.multiple
                })),
                fiscalBenefits:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].fiscalBenefits,
                description:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].destination.description // do industrialType
              };
            })
        };

        dispatch(orderSimulateRequest(param));
        const orderSimulate: ICheckoutResponse | Error =
          await checkoutServiceMvp.generateOrderSimulate(param);
        if (!(orderSimulate instanceof Error)) {
          dispatch(orderSimulateRequestSuccess(orderSimulate));
          dispatch(
            updateCheckoutLocalStorage({
              ...checkoutState,
              data: orderSimulate,
              params: param
            })
          );

          setValidationModalInformation((prev) => ({
            ...prev,
            show: false
          }));
          setGoToCheckout(true);
          history.push(`${EPurchaseRoutes.CHECKOUT}`);
          return;
        }
      }

      //END FUNCTION TO GO CHECKOUT
      setValidationModalInformation((prev) => ({
        ...prev,
        show: false
      }));

      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable({ noClean: true });
    } finally {
      setCartLoading(false);
    }
  };

  if ((!loading && itemsTable.items.length === 0) || !cartId) {
    return (
      <MessageChooseItem message="Seu carrinho de produtos está vazio.">
        <ValidationModal
          {...validationModalInformation}
          loading={loading}
          handleCloseValidationModal={handleCloseValidationModal}
        />
        <Button
          name="addProducts"
          onClick={() =>
            history.push(`${ERoutes.PURCHASE}/commerce/steel-type-choose`)
          }
        >
          Adicionar produtos
        </Button>
      </MessageChooseItem>
    );
  }

  const handlePrevious = () => {
    const menuLinksCache = localStorage.getItem(
      ELocalStorageKey.LONG_STEEL_MENU_NAVEGATION
    );
    const convertedLinks = JSON.parse(
      menuLinksCache ?? ''
    ) as unknown as BreadcrumbItemProps[];

    const link = convertedLinks.slice(
      convertedLinks.length - 2,
      convertedLinks.length - 1
    )[0];

    if (link?.url) {
      if (link.url === 'product') {
        return history.push(`${ERoutes.PURCHASE}/long-steel/commerce/catalog`);
      } else {
        return history.push(
          `${ERoutes.PURCHASE}/long-steel/commerce/${link.url}`
        );
      }
    }

    return history.push(`${ERoutes.PURCHASE}/commerce/steel-type-choose`);
  };

  const showLoadNewPriceButton = dirtyCartItems.filter((item) => {
    const cartItemCheck = cartItems.find(
      (cart) => cart.item_id === item.item_id
    );
    const itemAtt = item.extension_attributes;
    const cartItemAtt = cartItemCheck?.extension_attributes;

    const productDate = cartItemAtt?.expected_date_gerdau;
    const price = item.price;
    const salesAgreementData = price.salesAgreementData;
    const endDate = salesAgreementData.expirationDate
      ? salesAgreementData.expirationDate.replace('Z', '')
      : salesAgreementData.expirationDate;

    const diff =
      itemAtt.destination_gerdau !== cartItemAtt?.destination_gerdau ||
      itemAtt.payment_type_gerdau !== cartItemAtt?.payment_type_gerdau ||
      itemAtt.tax_benefit_gerdau !== cartItemAtt?.tax_benefit_gerdau ||
      (salesAgreementData && !!endDate && moment(productDate).isAfter(endDate));

    if (diff) {
      return item;
    }
  });

  return (
    <ShoppingCartContainer>
      <ValidationModal
        {...validationModalInformation}
        loading={loading}
        handleCloseValidationModal={handleCloseValidationModal}
      />
      <CartHeader
        productsQuantity={cartItems.length}
        graphs={generateGraph(cartItems)}
        loading={loading}
        showMinimunAlert={!loading && validations.minimun}
        showMaximunAlert={!loading && validations.maximun}
      />
      <CartProductTable
        shoppingCartRef={shoppingCartRef}
        selectedItems={selectedItems}
        allItems={cartItems}
        dirtyCartItems={dirtyCartItems}
        itemsTable={itemsTable}
        handleUpdateItem={handleUpdateItem}
        handleDuplicateItem={handleDuplicateItem}
        handleDeleteItem={handleDeleteItem}
        handleDeleteAllItems={handleDeleteAllItems}
        handleUpdateAllItems={handleUpdateAllItems}
        handleSelectAll={handleSelectAll}
        handleSelectProduct={handleSelectProduct}
        isBlockedCustomer={!!isBlockedCustomer}
        loading={loading}
        key={JSON.stringify(itemsTable.items.map((dirty) => dirty.item_id))}
      />
      <CartPagination
        handleChange={handleChangePagination}
        loading={loading}
        total={cartItems.length}
      />
      <SummaryBottom
        loading={loading}
        hasProducts={cartItems.length !== 0}
        totalPrice={getTotal(cartItems)}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        showLoadNewPriceButton={showLoadNewPriceButton.length > 0}
        onLoadNewPrice={handleUpdateAllItems}
        goToCheckout={goToCheckout}
      />
    </ShoppingCartContainer>
  );
};
export default ShoppingCartMvp;
